import { useCallback } from "react";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import {
  createFolder,
  updateFolderParent,
  renameFolder,
  renameChat,
  deleteFolder,
  deleteChat,
} from "../../services/api";
import type { Folder } from "../../services/api";
import type { FolderNode } from "./types";
import { findNode, findParent, isDescendant } from "./treeUtils";

/**
 * Logica:
 * - **Albero cartelle**: rename e delete chiamano endpoint folder con id della folder (node.data.id).
 * - **Albero conversazioni**: delete chiama delete chat con id della chat (node.id).
 * - **Move**: se trascino una chat (dalle conversazioni) nelle cartelle → crea un record Folder (type=chat).
 *            se trascino un elemento già nelle cartelle → aggiorna parent_id (updateFolderParent).
 */
export function useFolderTreeHandlers(
  folders: FolderNode<Folder>[],
  conversationsToday: FolderNode[],
  conversationsPast: FolderNode[],
  chats:
    | { id: string; title: string | null; update_date: string }[]
    | undefined,
  renameDialog: {
    mode: "rename" | "create";
    targetType: "folder" | "chat";
    id: string;
    currentName: string;
    parentId?: string;
  },
  setRenameDialog: (d: {
    open: boolean;
    mode: "rename" | "create";
    targetType: "folder" | "chat";
    id: string;
    currentName: string;
    parentId?: string;
  }) => void
) {
  const queryClient = useQueryClient();

  /** Verifica se il nodo appartiene all'albero cartelle (ha .data = Folder) */
  const isFolderTreeNode = (node: FolderNode): node is FolderNode<Folder> =>
    node.data != null && typeof (node.data as Folder).id === "string";

  const handleMove = useCallback(
    async (nodeToMove: FolderNode, targetId: string, insertAfter?: boolean) => {
      const nodeId = nodeToMove.id;
      if (nodeId === targetId) return;

      const isFromFolders = isFolderTreeNode(nodeToMove);
      const isFromToday = findNode(nodeId, conversationsToday) != null;
      const isFromPast = findNode(nodeId, conversationsPast) != null;

      let targetNode: FolderNode | null = null;
      let targetParent: FolderNode | null = null;
      let targetIndex = -1;
      let targetArray: FolderNode[] | null = null;
      let isTargetInFolders = false;
      let desiredOrder: number | undefined = undefined;

      if (targetId === "root-folders") {
        targetArray = folders;
        targetParent = null;
        isTargetInFolders = true;
        desiredOrder = folders.length;
        if (nodeToMove.type !== "folder") return;
      } else if (
        targetId === "today-section" ||
        targetId === "past-section" ||
        targetId === "root-conversations"
      ) {
        return;
      } else {
        targetNode =
          findNode(targetId, folders) ||
          findNode(targetId, conversationsToday) ||
          findNode(targetId, conversationsPast);
        if (!targetNode) return;

        const parentInfo =
          findParent(targetId, folders) ||
          findParent(targetId, conversationsToday) ||
          findParent(targetId, conversationsPast);
        if (parentInfo) {
          targetParent = parentInfo.parent;
          targetIndex = parentInfo.index;
          targetArray = targetParent.children || [];
          isTargetInFolders =
            findNode(targetParent.id, folders) != null ||
            targetParent.id === "root-folders";
        } else {
          const rootIndex = folders.findIndex((f) => f.id === targetId);
          if (rootIndex !== -1) {
            targetArray = folders;
            targetIndex = rootIndex;
            isTargetInFolders = true;
          } else return;
        }
      }

      if (
        nodeToMove.type === "folder" &&
        targetNode &&
        isDescendant(targetId, nodeToMove)
      )
        return;

      if (!isTargetInFolders) return;

      let newParentId: string | null = null;
      if (targetId === "root-folders") {
        newParentId = null;
      } else if (
        targetNode &&
        targetNode.type === "folder" &&
        insertAfter === undefined
      ) {
        newParentId = targetNode.id;
      } else if (targetParent) {
        newParentId = targetParent.id;
      }

      // Le chat (conversation) non possono stare in root: devono avere sempre una cartella parent reale.
      if (nodeToMove.type === "conversation") {
        const droppedInsideFolder =
          !!targetNode && targetNode.id !== "root-folders";

        if (
          !droppedInsideFolder ||
          !newParentId ||
          newParentId === "root-folders"
        ) {
          return;
        }
      }

      if (targetId !== "root-folders" && targetArray && targetIndex >= 0) {
        desiredOrder = targetIndex + (insertAfter ? 1 : 0);
      }

      const hasChatAlreadyInTargetFolderDirect = (
        targetParentId: string,
        chatId: string,
        movingFolderId?: string
      ): boolean => {
        const targetFolderNode = findNode(
          targetParentId,
          folders
        ) as FolderNode<Folder> | null;
        const directChildren =
          (targetFolderNode?.children as FolderNode<Folder>[] | undefined) ||
          [];
        return directChildren.some((child) => {
          const data = child.data as Folder | undefined;
          return (
            !!data &&
            data.type === 1 &&
            data.chat_id === chatId &&
            data.id !== movingFolderId
          );
        });
      };

      try {
        const movingFolderData = isFolderTreeNode(nodeToMove)
          ? (nodeToMove.data as Folder)
          : undefined;
        const movingChatId =
          movingFolderData?.type === 1
            ? movingFolderData.chat_id
            : (isFromToday || isFromPast) && nodeToMove.type === "conversation"
              ? nodeId
              : null;

        if (
          movingChatId &&
          newParentId &&
          hasChatAlreadyInTargetFolderDirect(
            newParentId,
            movingChatId,
            movingFolderData?.id
          )
        ) {
          toast.error("Questa chat è già presente nella cartella selezionata.");
          return;
        }

        if (isFromFolders && isFolderTreeNode(nodeToMove)) {
          // Nodo già nelle cartelle: aggiorna parent (endpoint folder)
          const folderId = (nodeToMove.data as Folder).id;
          await updateFolderParent(folderId, newParentId, desiredOrder);
          await queryClient.invalidateQueries("folders");
          await queryClient.refetchQueries("folders");
        } else if (
          (isFromToday || isFromPast) &&
          nodeToMove.type === "conversation"
        ) {
          // Chat trascinata dalle conversazioni nelle cartelle: crea record Folder (type=chat)
          const chat = chats?.find((c) => c.id === nodeId);
          if (chat) {
            await createFolder({
              name: chat.title || "Chat senza titolo",
              parent_id: newParentId,
              order: desiredOrder ?? null,
              type: 1,
              chat_id: nodeId,
            });
            await queryClient.invalidateQueries("folders");
            await queryClient.refetchQueries("folders");
            await queryClient.invalidateQueries("chats");
            await queryClient.refetchQueries("chats");
          }
        }
      } catch (error: any) {
        console.error("Error moving:", error);
        toast.error(error?.message || "Errore durante lo spostamento");
      }
    },
    [folders, conversationsToday, conversationsPast, chats, queryClient]
  );

  /** Rinomina: cartella o chat (sezione conversazioni). */
  const handleRenameClick = useCallback(
    (node: FolderNode) => {
      if (
        node.id.startsWith("root-") ||
        node.id === "today-section" ||
        node.id === "past-section"
      )
        return;

      // Nodi del primo albero (cartelle), inclusi type=chat, rinominano sempre via folder API
      if (isFolderTreeNode(node)) {
        const folderId = (node.data as Folder).id;
        setRenameDialog({
          open: true,
          mode: "rename",
          targetType: "folder",
          id: folderId,
          currentName: node.label,
        });
        return;
      }

      // Conversazioni del secondo albero rinominano il titolo chat sul BE AI
      if (node.type === "conversation") {
        setRenameDialog({
          open: true,
          mode: "rename",
          targetType: "chat",
          id: node.id,
          currentName: node.label,
        });
      }
    },
    [setRenameDialog]
  );

  const handleRenameConfirm = useCallback(
    async (newName: string) => {
      try {
        if (renameDialog.mode === "create") {
          await createFolder({
            name: newName,
            parent_id: renameDialog.parentId ?? null,
            type: 0,
          });
          toast.success("Cartella creata con successo");
          queryClient.invalidateQueries("folders");
          return;
        }

        const { id } = renameDialog;
        if (!id) return;
        if (renameDialog.targetType === "chat") {
          await renameChat(id, newName);
          toast.success("Chat rinominata con successo");
          queryClient.invalidateQueries("chats");
          queryClient.invalidateQueries("folders");
        } else {
          await renameFolder(id, newName);
          toast.success("Elemento rinominata con successo");
          queryClient.invalidateQueries("folders");
        }
      } catch (error: any) {
        if (renameDialog.mode === "create") {
          console.error("Error creating folder:", error);
          toast.error(
            error?.message || "Errore durante la creazione della cartella"
          );
        } else {
          console.error("Error renaming folder:", error);
          toast.error(error?.message || "Errore durante la rinomina");
        }
      }
    },
    [renameDialog, queryClient]
  );

  /**
   * Delete:
   * - Se nodo ha .data (è nell'albero cartelle) → deleteFolder(node.data.id).
   * - Se nodo è conversazione senza .data (albero conversazioni) → deleteChat(node.id).
   */
  const handleDelete = useCallback(
    async (node: FolderNode) => {
      const id = node.id;
      if (
        id.startsWith("root-") ||
        id === "today-section" ||
        id === "past-section"
      )
        return;

      if (isFolderTreeNode(node)) {
        const folderId = (node.data as Folder).id;
        try {
          await deleteFolder(folderId);
          toast.success("Elemento eliminato con successo");
          queryClient.invalidateQueries("folders");
        } catch (error: any) {
          console.error("Error deleting folder:", error);
          toast.error(
            error?.message || "Errore durante l'eliminazione della cartella"
          );
        }
      } else {
        try {
          await deleteChat(id);
          toast.success("Chat eliminata con successo");
          queryClient.invalidateQueries("chats");
        } catch (error: any) {
          console.error("Error deleting chat:", error);
          toast.error(
            error?.message || "Errore durante l'eliminazione della chat"
          );
        }
      }
    },
    [queryClient]
  );

  const handleCreateFolder = useCallback(
    (parentId?: string) => {
      setRenameDialog({
        open: true,
        mode: "create",
        targetType: "folder",
        id: "",
        currentName: "",
        parentId,
      });
    },
    [setRenameDialog]
  );

  return {
    handleMove,
    handleRenameClick,
    handleRenameConfirm,
    handleDelete,
    handleCreateFolder,
  };
}
